import { scaleBand, scaleLinear } from 'd3-scale';
import { useMemo } from 'react';
import { t } from '../../i18n/index.ts';
import { axisMoney, compactMoney, money, monthYear } from '../../lib/format.ts';
import { useWidth, type FlowYear } from '../../lib/hooks.ts';
import { moneyTicks } from './chartUtils.ts';
import { ScrubOverlay, useScrub } from './useScrub.ts';

type FlowKey = 'in' | 'out' | 'kept' | 'growth';

const FLOW: Readonly<Record<FlowKey, { color: string; label: () => string }>> = {
  in: { color: 'var(--mint)', label: () => t('forecast.cashflow.in') },
  out: { color: 'var(--amber)', label: () => t('forecast.cashflow.out') },
  kept: { color: 'var(--violet)', label: () => t('forecast.cashflow.kept') },
  growth: { color: 'var(--cyber)', label: () => t('forecast.cashflow.growth') },
};

const KEYS: readonly FlowKey[] = ['in', 'out', 'kept', 'growth'];

/** "From Oct 2026" for a first year that starts this month, else "2027". */
export const flowYearLabel = (y: FlowYear): string => (y.partial ? t('forecast.cashflow.fromMonth', { month: monthYear(y.from) }) : String(y.year));

export const flowValuetext = (y: FlowYear): string =>
  t('forecast.cashflow.valuetext', { year: y.year, in: money(y.in), out: money(y.out), kept: money(y.kept, { sign: true }), growth: money(y.growth, { sign: true }) });

/**
 * Per year: money in (up) and out (down) side by side with what stayed yours (kept) and growth, so
 * in − out is the change in cash and all four add up to the change in net worth (R22).
 */
export function CashflowChart({ years, height, compact = false }: { years: readonly FlowYear[]; height: number; compact?: boolean }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const margin = compact ? { top: 10, right: 8, bottom: 24, left: 48 } : { top: 14, right: 14, bottom: 28, left: 58 };
  const innerW = Math.max(0, width - margin.left - margin.right);
  const innerH = Math.max(0, height - margin.top - margin.bottom);

  const layout = useMemo(() => {
    if (years.length === 0 || innerW <= 0) return null;
    const up = (y: FlowYear) => Math.max(y.in, Math.max(0, y.kept) + Math.max(0, y.growth));
    const down = (y: FlowYear) => Math.max(y.out, -Math.min(0, y.kept) - Math.min(0, y.growth));
    const { ticks, lo, hi } = moneyTicks(-Math.max(...years.map(down)), Math.max(...years.map(up)), 4);
    const x = scaleBand<number>().domain(years.map((y) => y.year)).range([0, innerW]).paddingInner(years.length > 20 ? 0.2 : 0.3).paddingOuter(0.1);
    const y = scaleLinear().domain([lo, hi]).range([innerH, 0]);
    const labelEvery = Math.max(1, Math.ceil(years.length / Math.floor(innerW / (compact ? 40 : 56))));
    const xs = years.map((yr) => (x(yr.year) ?? 0) + x.bandwidth() / 2);
    return { x, y, ticks, labelEvery, xs };
  }, [years, innerW, innerH, compact]);

  const { index, handlers } = useScrub(layout?.xs ?? []);
  const active = index !== null ? years[index] : undefined;
  const label = t('forecast.cashflowChart.label', { from: years[0]?.year ?? '', to: years[years.length - 1]?.year ?? '' });

  const bar = (key: FlowKey, x: number, w: number, from: number, to: number) => {
    if (!layout || from === to) return null;
    const top = layout.y(Math.max(from, to));
    return <rect key={key} x={x} y={top} width={w} height={Math.max(0, layout.y(Math.min(from, to)) - top)} fill={FLOW[key].color} fillOpacity={0.85} />;
  };

  return (
    <figure className="chart">
      <div ref={ref} className="chart__frame" style={{ height }}>
        {layout && (
          <svg width={width} height={height} aria-hidden="true">
            <g transform={`translate(${margin.left},${margin.top})`}>
              {layout.ticks.map((tick) => (
                <g key={tick}>
                  <line x1={0} x2={innerW} y1={layout.y(tick)} y2={layout.y(tick)} className={tick === 0 ? 'chart__gridline chart__gridline--base' : 'chart__gridline'} />
                  <text x={-10} y={layout.y(tick)} dy="0.34em" textAnchor="end" className="chart__axis-label">{axisMoney(tick)}</text>
                </g>
              ))}
              <g className="chart__grow">
                {years.map((yr, i) => {
                  const bx = layout.x(yr.year) ?? 0;
                  const half = layout.x.bandwidth() / 2;
                  const gap = Math.min(2, half / 4);
                  const w = half - gap / 2;
                  const keptUp = yr.kept >= 0;
                  return (
                    <g key={yr.year} opacity={index === null || index === i ? 1 : 0.45}>
                      {bar('in', bx, w, 0, yr.in)}
                      {bar('out', bx, w, 0, -yr.out)}
                      {bar('kept', bx + half + gap / 2, w, 0, yr.kept)}
                      {bar('growth', bx + half + gap / 2, w, keptUp === yr.growth >= 0 ? yr.kept : 0, (keptUp === yr.growth >= 0 ? yr.kept : 0) + yr.growth)}
                    </g>
                  );
                })}
              </g>
              {years.map((yr, i) =>
                i % layout.labelEvery === 0 ? (
                  <text key={yr.year} x={layout.xs[i]} y={innerH + (compact ? 16 : 20)} textAnchor="middle" className="chart__axis-label">{yr.year}</text>
                ) : null,
              )}
            </g>
          </svg>
        )}
        {layout && (
          <ScrubOverlay
            left={margin.left} top={margin.top} width={innerW} height={innerH} label={label} max={years.length - 1} index={index} handlers={handlers}
            valuetext={active ? flowValuetext(active) : undefined}
          />
        )}
        {active && layout && (
          <div
            className="chart-tooltip"
            style={{
              left: margin.left + layout.xs[index!]! + (layout.xs[index!]! > innerW * 0.6 ? -14 : 14),
              top: margin.top + 4,
              transform: layout.xs[index!]! > innerW * 0.6 ? 'translateX(-100%)' : undefined,
            }}
            aria-hidden="true"
          >
            <span className="chart-tooltip__date">{flowYearLabel(active)}</span>
            {KEYS.map((k) => (
              <span key={k} className="chart-tooltip__row">
                <i style={{ background: FLOW[k].color }} />{FLOW[k].label()} <b>{compactMoney(active[k], { sign: k === 'kept' || k === 'growth' })}</b>
              </span>
            ))}
            <span className="chart-tooltip__row chart-tooltip__row--total">{t('forecast.cashflow.change')} <b>{compactMoney(active.netWorthChange, { sign: true })}</b></span>
          </div>
        )}
      </div>
      {years[0] && (
        <figcaption className="chart-start-note" data-testid="cashflow-note">
          {t('forecast.cashflow.note', { year: years[0].year, month: monthYear(years[0].from) })}
        </figcaption>
      )}
    </figure>
  );
}

export const CASHFLOW_LEGEND = KEYS.map((k) => FLOW[k]);
